import type { BandwidthProgress } from '../lib/bandwidth-test';

export type TestPhase = 'idle' | 'speed' | 'diagnostic' | 'completed' | 'error';

interface RunningStateProps {
  phase: TestPhase;
  bandwidthProgress: BandwidthProgress | null;
  diagProgress: { testId: string; progress: number; message: string } | null;
  currentSpeed: number;
}

export function RunningState({ phase, diagProgress }: RunningStateProps) {
  console.log('[RunningState] Rendering', {
    phase,
    hasDiagProgress: !!diagProgress,
  });

  const testNames: Record<string, string> = {
    A: 'Baseline (Single Connection)',
    B: 'Parallel Downloads',
    C: 'Small Files Burst',
    D: 'Sustained Transfer',
  };

  // Unique colors per test matching Methodology docs
  const testColors: Record<
    string,
    { bg: string; text: string; border: string; pulse: string; bar: string }
  > = {
    A: {
      bg: 'bg-ds-primary/20',
      text: 'text-ds-primary',
      border: 'border-ds-primary/30',
      pulse: 'bg-ds-primary',
      bar: 'bg-ds-primary',
    },
    B: {
      bg: 'bg-ds-accent/20',
      text: 'text-ds-accent',
      border: 'border-ds-accent/30',
      pulse: 'bg-ds-accent',
      bar: 'bg-ds-accent',
    },
    C: {
      bg: 'bg-ds-secondary/20',
      text: 'text-ds-secondary',
      border: 'border-ds-secondary/30',
      pulse: 'bg-ds-secondary',
      bar: 'bg-ds-secondary',
    },
    D: {
      bg: 'bg-pink-500/20',
      text: 'text-pink-500',
      border: 'border-pink-500/30',
      pulse: 'bg-pink-500',
      bar: 'bg-pink-500',
    },
  };

  const currentTestColor = diagProgress
    ? testColors[diagProgress.testId]
    : testColors.A;

  return (
    <div className="text-center w-full max-w-[720px] mx-auto px-4">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          Running Diagnostics
        </h2>
        <p className="text-gray-400">Testing your network performance...</p>
      </div>

      {/* Current test indicator */}
      {phase === 'diagnostic' && diagProgress && (
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-ds-dark-lighter rounded-lg">
            <div
              className={`w-2 h-2 rounded-full ${currentTestColor.pulse} animate-pulse`}
            />
            <span className={`font-medium ${currentTestColor.text}`}>
              Test {diagProgress.testId}: {testNames[diagProgress.testId]}
            </span>
          </div>

          <p className="text-gray-400 text-sm">{diagProgress.message}</p>

          <div className="max-w-md mx-auto">
            <div className="w-full bg-ds-dark-lighter rounded-full h-2">
              <div
                className={`${currentTestColor.bar} h-2 rounded-full transition-all`}
                style={{ width: `${diagProgress.progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {Math.round(diagProgress.progress)}% complete
            </p>
          </div>
        </div>
      )}

      {/* Test overview */}
      <div className="mt-8 grid grid-cols-4 gap-2 max-w-md mx-auto">
        {(['A', 'B', 'C', 'D'] as const).map((testId) => {
          const isActive = diagProgress?.testId === testId;
          const isComplete = diagProgress && testId < diagProgress.testId;
          const color = testColors[testId];

          return (
            <div
              key={testId}
              className={`py-2 px-3 rounded text-xs font-medium ${
                isActive
                  ? `${color.bg} ${color.text} border ${color.border}`
                  : isComplete
                    ? `${color.bg} ${color.text}`
                    : 'bg-ds-dark-lighter text-gray-500'
              }`}
            >
              Test {testId}
            </div>
          );
        })}
      </div>
    </div>
  );
}
